import { useRequest } from 'ahooks';
import AUTH_API from 'api/auth';
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';
import { persistState, getPersistedState } from 'utils/persist-util';

export interface IAuthState {
  authed: boolean;
  token: string;
  refreshToken: string;
  firstName: string;
  lastName: string;
  isAdmin: boolean;
}

interface IAuthContext {
  authState: IAuthState;
  setAuthState: React.Dispatch<React.SetStateAction<IAuthState>>;
}

export const AuthContext = createContext<IAuthContext>({
  authState: {
    authed: false,
    token: '',
    refreshToken: '',
    firstName: '',
    lastName: '',
    isAdmin: false,
  },
  setAuthState: () => {},
});

interface IAuthWrapper {
  children: React.ReactNode;
}

export function AuthWrapper({ children }: IAuthWrapper) {
  const initMount = useRef(true);
  const [authState, setAuthState] = useState<IAuthState>(
    getPersistedState(process.env.REACT_APP_PERSIST_AUTH) || { authed: false }
  );

  useEffect(() => {
    if (!initMount.current) {
      persistState(process.env.REACT_APP_PERSIST_AUTH || '', authState);
    } else {
      initMount.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState]);

  return (
    <AuthContext.Provider value={{ authState, setAuthState }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const { authState, setAuthState } = useContext(AuthContext);
  const [loginErrorDialog, setLoginErrorDialog] = useState(false);

  const closeDialog = () => {
    setLoginErrorDialog(false);
  };

  // login useRequest
  const {
    run: login,
    loading,
    error,
  } = useRequest(AUTH_API.postLogin, {
    manual: true,
    onSuccess: (loginData) =>
      setAuthState({
        token: loginData.accessToken,
        refreshToken: loginData.refreshToken,
        authed: true,
        firstName: loginData.firstName,
        lastName: loginData.lastName,
        isAdmin: loginData.isAdmin,
      }),
    onError: () => {
      setLoginErrorDialog(true);
      setAuthState({
        token: '',
        refreshToken: '',
        authed: false,
        firstName: '',
        lastName: '',
        isAdmin: false,
      });
    },
  });

  const logout = () => {
    setAuthState({
      authed: false,
      token: '',
      refreshToken: '',
      firstName: '',
      lastName: '',
      isAdmin: false,
    });
  };

  return {
    authState,
    setAuthState,
    login,
    logout,
    loading,
    error,
    loginDialog: loginErrorDialog,
    closeDialog,
  };
}
